import React from 'react'
import { View, Text } from '@react-pdf/renderer'
import { styles, columnWidths } from '../styles'
import { PDFTableProps } from '../types'


export function PDFTable({ 
  headers, 
  rows, 
  columnWidths: customColumnWidths,
  headerStyle,
  rowStyle,
  alternateRowStyle,
  cellStyle,
  compact = false
}: PDFTableProps) {
  // Calculate column widths - use custom widths or distribute evenly
  const colWidths = customColumnWidths || headers.map(() => `${100/headers.length}%`)
  
  // Select appropriate styles based on compact mode
  const tableStyle = compact ? styles.compactTable : styles.table
  const headerRowStyle = compact ? styles.compactTableHeader : styles.tableHeader
  const headerCellStyle = compact ? styles.compactTableHeaderCell : styles.tableCellHeader
  const defaultRowStyle = compact ? styles.compactTableRow : styles.tableRow
  const defaultCellStyle = compact ? styles.compactTableCell : styles.tableCell
  
  return (
    <View style={tableStyle}>
      {/* Header Row */}
      <View style={[headerRowStyle, headerStyle]}>
        {headers.map((header, i) => (
          <View 
            key={i} 
            style={[
              headerCellStyle,
              { width: colWidths[i] },
              ...(i === headers.length - 1 ? [styles.tableCellLast] : [])
            ]}
          >
            <Text>{header}</Text>
          </View>
        ))}
      </View>
      
      {/* Data Rows */}
      {rows.map((row, rowIndex) => {
        const isAlternate = rowIndex % 2 === 1
        const isAidStation = Array.isArray(row) && row.some((cell, cellIndex) => {
          return typeof cell === 'string' && cell.includes('AS:')
        })
        
        let currentRowStyle = defaultRowStyle
        if (isAidStation && !compact) {
          currentRowStyle = styles.tableRowAidStation
        } else if (isAlternate && alternateRowStyle && !compact) {
          currentRowStyle = styles.tableRowAlternate
        }
        
        return (
          <View 
            key={rowIndex} 
            style={[currentRowStyle, rowStyle]}
          >
            {Array.isArray(row) && row.map((cell, colIndex) => (
              <View 
                key={colIndex} 
                style={[
                  defaultCellStyle,
                  { width: colWidths[colIndex] },
                  cellStyle,
                  ...(colIndex === row.length - 1 ? [styles.tableCellLast] : [])
                ]}
              >
                {typeof cell === 'string' ? (
                  <Text>{cell}</Text>
                ) : (
                  cell
                )}
              </View>
            ))}
          </View>
        )
      })}
    </View>
  )
}


// Specialized table variants for common use cases
export function FullPlanTable({ 
  segments, 
  calculateSegmentPace,
  formatTime,
  getSegmentNutrition 
}: {
  segments: any[]
  calculateSegmentPace: (segment: any) => number
  formatTime: (minutes: number) => string
  getSegmentNutrition: (segmentId: string) => any
}) {
  let cumulativeDistance = 0
  let cumulativeTime = 0
  
  const headers = [
    '#', 'Segment', 'Dist (km)', 'D+ (m)', 'D- (m)', 
    'Net Elev', 'Pace (min/km)', 'Time', 'Cum Dist', 'ETA', 
    'Carbs', 'Fluids'
  ]
  
  const colWidths = Object.values(columnWidths.fullPlan)
  
  const rows = segments.map((segment, index) => {
    cumulativeDistance += segment.distance_km
    const pace = calculateSegmentPace(segment)
    const segmentTime = (pace * segment.distance_km) + (segment.planned_stop_min || 0)
    cumulativeTime += segmentTime
    
    const segmentNutrition = getSegmentNutrition(segment.id)
    
    // Format elevation changes with visual indicators
    const elevGain = segment.elev_gain_m ? `+${segment.elev_gain_m}m` : '-'
    const elevLoss = segment.elev_loss_m ? `-${segment.elev_loss_m}m` : '-'
    const elevNet = (segment.elev_gain_m || 0) - (segment.elev_loss_m || 0)
    const elevNetDisplay = elevNet > 0 ? `↗ +${elevNet}m` : elevNet < 0 ? `↘ ${elevNet}m` : '→ 0m'
    
    // Format time display with stop time
    const timeDisplay = segment.planned_stop_min 
      ? `${formatTime(pace * segment.distance_km)} (+${segment.planned_stop_min}m)`
      : formatTime(segmentTime)
    
    return [
      (index + 1).toString(),
      segment.label || `Segment ${index + 1}`,
      segment.distance_km.toFixed(2),
      elevGain,
      elevLoss,
      elevNetDisplay,
      pace.toFixed(2),
      timeDisplay,
      cumulativeDistance.toFixed(2),
      `T+${formatTime(cumulativeTime)}`,
      `${segmentNutrition.carbs_g}g`,
      `${segmentNutrition.fluids_ml}ml`
    ]
  })
  
  return (
    <PDFTable 
      headers={headers}
      rows={rows}
      columnWidths={colWidths}
      compact={false}
    />
  )
}

export function CompactPaceTable({ 
  timelineEvents,
  formatTime 
}: {
  timelineEvents: any[]
  formatTime: (minutes: number) => string
}) {
  const headers = ['Checkpoint', 'Km', 'Elev', 'ETA', 'Nutrition']
  const colWidths = ['22%', '13%', '15%', '20%', '30%']
  
  const rows = timelineEvents.map(item => {
    if (item.type === 'segment') {
      const segment = item.data
      const segmentName = segment.label || `${segment.segmentEnd.toFixed(1)}km`
      
      // Show both elevation gain and loss
      const elevGain = segment.elev_gain_m || 0
      const elevLoss = segment.elev_loss_m || 0
      let elevDisplay = '-'
      
      if (elevGain > 0 && elevLoss > 0) {
        elevDisplay = `+${elevGain}m | -${elevLoss}m`
      } else if (elevGain > 0) {
        elevDisplay = `+${elevGain}m`
      } else if (elevLoss > 0) {
        elevDisplay = `-${elevLoss}m`
      }
      
      return [
        segmentName,
        segment.segmentEnd.toFixed(1),
        elevDisplay,
        formatTime(item.cumulativeTime),
        item.nutritionDisplay || '-'
      ]
    } else {
      // Aid station row
      const station = item.data
      const stationStopTime = station.planned_stop_min || 0
      const exitTime = item.cumulativeTime + stationStopTime
      const etaDisplay = stationStopTime > 0 
        ? `In: ${formatTime(item.cumulativeTime)} | Out: ${formatTime(exitTime)}`
        : formatTime(item.cumulativeTime)
      
      return [
        `AS: ${station.name}`,
        station.at_distance_km.toFixed(1),
        '-', // No elevation for aid stations
        etaDisplay,
        item.nutritionDisplay || '-'
      ]
    }
  })

  return (
    <View style={styles.compactTable}>
      {/* Header */}
      <View style={styles.compactTableHeader}>
        {headers.map((header, i) => (
          <View 
            key={i} 
            style={[
              styles.compactTableHeaderCell,
              { width: colWidths[i] },
              i === headers.length - 1 ? styles.compactTableCellLast : {}
            ]}
          >
            <Text>{header.toUpperCase()}</Text>
          </View>
        ))}
      </View>
      
      {/* Data Rows */}
      {rows.map((row, rowIndex) => (
        <View key={rowIndex} style={styles.compactTableRow}>
          {Array.isArray(row) && row.map((cell, colIndex) => (
            <View 
              key={colIndex} 
              style={[
                styles.compactTableCell,
                { width: colWidths[colIndex] },
                colIndex === row.length - 1 ? styles.compactTableCellLast : {}
              ]}
            >
              <Text style={{ fontSize: 8 }}>{cell}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  )
}

export function AidStationsTable({ 
  aidStations, 
  planItems 
}: {
  aidStations: any[]
  planItems: any[]
}) {
  const headers = [
    'Station Name', 'Distance', 'Available Supplies', 'Planned Stop', 'Planned Items'
  ]
  
  const colWidths = ['25%', '15%', '25%', '15%', '20%']
  
  const rows = aidStations.map(station => {
    const stationItems = planItems.filter(item => item.aid_station_id === station.id)
    const available = (station.available as string[]) || []
    
    // Group planned items by type
    const plannedSnacks = stationItems.filter(item => item.type === 'snack').map(item => item.name)
    const plannedDrinks = stationItems.filter(item => item.type === 'drink').map(item => 
      item.fluids_ml ? `${item.name} (${item.fluids_ml}ml)` : item.name
    )
    
    const plannedItemsDisplay = [
      ...plannedSnacks.map(item => `Food: ${item}`),
      ...plannedDrinks.map(item => `Drink: ${item}`)
    ].join(', ') || '-'
    
    return [
      <View key={station.id}>
        <Text style={styles.textBold}>{station.name}</Text>
        {station.notes && (
          <Text style={[styles.textSmall, styles.textSecondary]}>
            {station.notes}
          </Text>
        )}
      </View>,
      `${station.at_distance_km.toFixed(2)}km`,
      available.length > 0 ? available.join(', ') : 'TBD',
      (station.planned_stop_min || 0) > 0 ? `${station.planned_stop_min}min` : 'No stop',
      plannedItemsDisplay
    ]
  })
  
  return (
    <PDFTable 
      headers={headers}
      rows={rows}
      columnWidths={colWidths}
      compact={false}
    />
  )
}